import { useCallback, useId, useRef, useState } from "react";
import { useClickOutside } from "../hooks/useClickOutside";
import dropdownChevron from "../assets/dropdown-chevron.svg";

export interface BannerOption {
  value: string;
  text: string;
}

interface BannerSelectProps {
  options: readonly BannerOption[];
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
}

const BannerSelect = ({
  options,
  value,
  onChange,
  error = false,
  helperText,
}: BannerSelectProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const labelId = useId();
  const listboxId = useId();
  const selected = options.find((option) => option.value === value);
  const label = selected?.text ?? "Choose a banner option";
  const isPlaceholder = !value;

  const closeMenu = useCallback(() => setMenuOpen(false), []);
  useClickOutside(containerRef, closeMenu, menuOpen);

  return (
    <div className="flex flex-col gap-1 w-full min-w-0 max-w-full">
      <span id={labelId} className="form-field-label">
        What type of banner is it? *
      </span>
      <div
        ref={containerRef}
        className={`form-input-frame relative${error ? " is-error" : ""}`}
      >
        <button
          type="button"
          className="flex items-center gap-2 w-full min-w-0 bg-transparent border-0 p-0 cursor-pointer"
          aria-haspopup="listbox"
          aria-expanded={menuOpen}
          aria-controls={listboxId}
          aria-labelledby={labelId}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span
            className={`flex-1 min-w-0 text-left text-base leading-6 truncate ${
              isPlaceholder ? "text-roller-blue-40" : "text-white"
            }`}
          >
            {label}
          </span>
          <span
            className="inline-flex w-5 h-5 items-center justify-center shrink-0"
            aria-hidden="true"
          >
            <img
              src={dropdownChevron}
              alt=""
              width={10}
              height={6}
              className="w-2.5 h-1.5"
            />
          </span>
        </button>

        {menuOpen ? (
          <div
            id={listboxId}
            role="listbox"
            aria-labelledby={labelId}
            className="dropdown-panel absolute top-[calc(100%+6px)] left-0 right-0 z-30 max-h-[260px] overflow-y-auto"
          >
            {options
              .filter((option) => option.value)
              .map((option) => {
                const isSelected = option.value === value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`dropdown-option${isSelected ? " is-selected" : ""}`}
                    onClick={() => {
                      onChange(option.value);
                      closeMenu();
                    }}
                  >
                    {option.text}
                  </button>
                );
              })}
          </div>
        ) : null}
      </div>
      {error && helperText ? (
        <p className="form-field-error" role="alert">
          {helperText}
        </p>
      ) : null}
    </div>
  );
};

export default BannerSelect;
